import {
  DRAFT_DOCUMENT,
  DRAFT_DOCUMENT_SUCCESS,
  DRAFT_DOCUMENT_FAILURE,
  CLEAR_DOCUMENT_DRAFT,
  COWRITE,
  COWRITE_SUCCESS,
  COWRITE_FAILURE,
  EVIDENCE_SEARCH,
  EVIDENCE_SEARCH_SUCCESS,
  EVIDENCE_SEARCH_FAILURE,
  EVIDENCE_DETAIL,
  EVIDENCE_DETAIL_SUCCESS,
  EVIDENCE_DETAIL_FAILURE,
  APPLY_COWRITE,
  APPLY_COWRITE_SUCCESS,
  APPLY_COWRITE_FAILURE,
  REFINE_FIELD,
  REFINE_FIELD_SUCCESS,
  REFINE_FIELD_FAILURE,
  FETCH_AUTHORING_HISTORY,
} from "./constants";

/**
 * Request an AI draft for a document node.
 * @param {{residentId:string, templateId:string, carePlanId?:string, sectionId?:string, locationId?:(number|string)}} payload
 * @param {(data:object)=>void} [onSuccess] receives the draft payload (may carry ai_unavailable)
 * @param {(error:any)=>void} [onError] transport/server error (not the soft-fail path)
 */
export const draftDocument = (payload, onSuccess, onError) => ({
  type: DRAFT_DOCUMENT,
  payload,
  onSuccess,
  onError,
});

export const draftDocumentSuccess = (payload) => ({
  type: DRAFT_DOCUMENT_SUCCESS,
  payload,
});

export const draftDocumentFailure = (payload) => ({
  type: DRAFT_DOCUMENT_FAILURE,
  payload,
});

export const clearDocumentDraft = () => ({
  type: CLEAR_DOCUMENT_DRAFT,
});

/**
 * Conversational co-write turn.
 * @param {{residentId:string, templateId:string, carePlanId?:string, target?:string, action:'discuss'|'draft', messages:Array, locationId?:(number|string)}} payload
 * @param {(data:object)=>void} [onSuccess]
 * @param {(error:any)=>void} [onError]
 */
export const cowrite = (payload, onSuccess, onError) => ({
  type: COWRITE,
  payload,
  onSuccess,
  onError,
});
export const cowriteSuccess = (payload) => ({ type: COWRITE_SUCCESS, payload });
export const cowriteFailure = (payload) => ({ type: COWRITE_FAILURE, payload });

/**
 * Deterministic evidence search — pulls resident items NOT in the default draft
 * bundle (older observations, other documents, incidents…) for the "Add more
 * evidence" picker. Free (no AI call, no credit); resident-scoped server-side.
 * Delivered via onSuccess like the other read paths — no reducer state.
 * @param {{residentId:string, templateId:string, carePlanId?:string, category?:string, q?:string, page?:(number|string)}} payload
 * @param {(data:object)=>void} [onSuccess] receives the search result payload ({ rows, has_more, ... })
 * @param {(error:any)=>void} [onError]
 */
export const evidenceSearch = (payload, onSuccess, onError) => ({
  type: EVIDENCE_SEARCH,
  payload,
  onSuccess,
  onError,
});
export const evidenceSearchSuccess = (payload) => ({
  type: EVIDENCE_SEARCH_SUCCESS,
  payload,
});
export const evidenceSearchFailure = (payload) => ({
  type: EVIDENCE_SEARCH_FAILURE,
  payload,
});

/**
 * Deterministic evidence detail — reads a single evidence item's actual content
 * (title, meta pairs, body paragraphs) for the panel's per-row detail slide-down.
 * Free (no AI call, no credit); resident-scoped server-side. Delivered via
 * onSuccess like the other read paths — no reducer state.
 * @param {{residentId:string, templateId:string, carePlanId?:string, kind:string, id?:(number|string|null), locationId?:(number|string)}} payload
 * @param {(data:object)=>void} [onSuccess] receives { action, detail } — detail may be null
 * @param {(error:any)=>void} [onError]
 */
export const evidenceDetail = (payload, onSuccess, onError) => ({
  type: EVIDENCE_DETAIL,
  payload,
  onSuccess,
  onError,
});
export const evidenceDetailSuccess = (payload) => ({
  type: EVIDENCE_DETAIL_SUCCESS,
  payload,
});
export const evidenceDetailFailure = (payload) => ({
  type: EVIDENCE_DETAIL_FAILURE,
  payload,
});

/**
 * Persist approved co-write schedules & tasks for a care plan version.
 * @param {{residentId:string, carePlanId:string, schedule:Array, tasks:Array}} payload
 * @param {(data:object)=>void} [onSuccess] receives { schedule_created, tasks_created, tasks_skipped }
 * @param {(error:any)=>void} [onError]
 */
export const applyCowrite = (payload, onSuccess, onError) => ({
  type: APPLY_COWRITE,
  payload,
  onSuccess,
  onError,
});
export const applyCowriteSuccess = (payload) => ({
  type: APPLY_COWRITE_SUCCESS,
  payload,
});
export const applyCowriteFailure = (payload) => ({
  type: APPLY_COWRITE_FAILURE,
  payload,
});

/**
 * Inline "✦ Refine" — a light copy-edit of one field's current text. Cheapest AI
 * call (no evidence graph, no citations); charges the care_planning wallet like a
 * small draft. Soft-fails (data.ai_unavailable) when depleted.
 * @param {{residentId:string, templateId:string, carePlanId?:string, fieldType:'text'|'list', value:(string|string[]), fieldLabel?:string, sectionTitle?:string, planContext?:string, locationId?:(number|string)}} payload
 * @param {(data:object)=>void} [onSuccess] receives { refined, items, ai_unavailable, ... }
 * @param {(error:any)=>void} [onError]
 */
export const refineField = (payload, onSuccess, onError) => ({
  type: REFINE_FIELD,
  payload,
  onSuccess,
  onError,
});
export const refineFieldSuccess = (payload) => ({
  type: REFINE_FIELD_SUCCESS,
  payload,
});
export const refineFieldFailure = (payload) => ({
  type: REFINE_FIELD_FAILURE,
  payload,
});

/**
 * Fetch the AI authoring history (draft/refine trail) for a document — newest
 * first. Read-only; result delivered via onSuccess (the panel holds it locally).
 * @param {{residentId:string, templateId:string}} payload
 * @param {(events:Array)=>void} [onSuccess]
 * @param {(error:any)=>void} [onError]
 */
export const fetchAuthoringHistory = (payload, onSuccess, onError) => ({
  type: FETCH_AUTHORING_HISTORY,
  payload,
  onSuccess,
  onError,
});
